/**
 * Orca layout route — the server-side admin gate for every `/orca/*` screen,
 * plus the public orcaloops.ai landing at the root.
 *
 * TanStack's `route.tsx` convention makes this the parent of every file under
 * `routes/orca/`, so one `beforeLoad` covers the dashboard, runners, repos,
 * projects, events, defaults, task detail and the runner-install docs page.
 * The login and sign-up doors (plus Clerk's steps beneath them) and the two
 * legal documents (`/orca/privacy`, `/orca/terms`) are the only unconditionally
 * public paths (see `isOrcaPublicPath`); everything else awaits
 * `requireAdmin`, which sends a signed-out visitor to the orca login and a
 * signed-in non-admin to `/access-denied` — mirroring
 * `routes/clawinstall/admin_/resellers/route.tsx`.
 *
 * The ROOT (`/orca`, rendered as `orcaloops.ai/`) is the one exception: a
 * visitor with no session gets the marketing landing instead of a bounce to the
 * login door, so the product has a front page. The session is read through
 * `optionalAuth` (Clerk-verified, never redirecting) — NOT the root context's
 * cookie-presence `serverAuth`. A signed-in root visitor still goes through
 * `requireAdmin` exactly as before: admins see the Board, non-admins land on
 * `/access-denied`.
 *
 * The `component` (in `route.lazy.tsx`, so the landing and the frame stay out
 * of the entry chunk) renders the landing or the matched child; the Board's own
 * `beforeLoad` is anonymous-safe (`orca-ssr.ts` returns empty dehydrated atoms
 * with no `__session` cookie), so a signed-out root visitor calls no admin RPC.
 *
 * @since 1.0.0
 */
import { createFileRoute } from "@tanstack/react-router";

import { ORCA_ADMIN_STYLESHEET_LINKS } from "~/components/design/orca-admin/stylesheets.ts";
import orcaDocCss from "~/components/orca/orca-doc.css?url";
import { isOrcaPublicPath } from "~/lib/orca-public-paths.ts";
import { orcaloopsStylesheetLinks } from "~/lib/orcaloops-head.ts";
import { requireAdmin } from "~/lib/server-admin.ts";
import { optionalAuth } from "~/lib/server-auth.ts";

/**
 * The orca root as the router sees it: `/orca/` on a first load of
 * `orcaloops.ai/` (the domain router prepends the prefix, keeping the trailing
 * slash) and `/orca` on a client-side navigation.
 *
 * Kept local on purpose — `isOrcaPublicPath` means "public whatever the
 * session", and the root is only public when there is NO session.
 *
 * @since 1.0.0
 */
export const isOrcaRootPath = (pathname: string): boolean => pathname === "/orca" || pathname === "/orca/";

/**
 * The guard, extracted so it is unit-testable without the route machinery.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const orcaLayoutBeforeLoad = async ({ location }: { readonly location: { readonly pathname: string } }) => {
  if (isOrcaPublicPath(location.pathname)) {
    return {};
  }
  if (isOrcaRootPath(location.pathname)) {
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- the session reader is a server function returning a Promise
    const session = await optionalAuth();
    if (session === null) {
      return { auth: null };
    }
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAdmin();
  return { auth };
};

/**
 * Head links for every `/orca/*` page: the `orca-admin` design slug's scoped
 * sheets first (the admin screens render those frames from `.lazy.tsx` chunks,
 * whose side-effect CSS never reaches the SSR head — packages/web/CLAUDE.md →
 * Performance budgets, rule 6; the small archive sheet rides along rather than
 * being linked per route), then, for the signed-out landing the lazy half
 * renders, the orcaloops frame sheet and the reading-column sheet. TanStack
 * merges every matched route's `head().links` and dedupes identical tags, so a
 * child page linking the same sheets collapses onto these.
 *
 * @since 1.0.0
 */
export const orcaLayoutHead = () => ({
  links: [...ORCA_ADMIN_STYLESHEET_LINKS, ...orcaloopsStylesheetLinks, { rel: "stylesheet", href: orcaDocCss }],
});

export const Route = createFileRoute("/orca")({
  beforeLoad: orcaLayoutBeforeLoad,
  head: orcaLayoutHead,
});
